// The media viewer's touch gestures (MessageList.tsx's Lightbox), on pointer events: a drag sideways pages a set, a pull
// down closes, and a picture also zooms (#371): two fingers pinch, a double tap goes in and out, and one finger pans a
// zoomed picture. The rules and numbers are lightboxLogic.ts; this file only reads fingers and moves the element.
// A mouse is left alone (the buttons and keys do its work). The picture has `touch-action: none` (styles.css) so the
// browser doesn't also pan or zoom it; the page around it keeps its own rules.
import { useEffect, useRef } from "react";
import {
  closesOnPull, doubleTapView, FIT_VIEW, pageAfterDrag, pageOffset, panView, pinchView, settleView, zoomedDragAxis, type ZoomBox, type ZoomDrag,
  type ZoomView,
} from "./lightboxLogic.ts";

interface Pt {
  x: number;
  y: number;
}
interface Drag {
  /** Where the finger is counted from: the first touch, moved to where the drag was decided for a pull or a pan. */
  x: number;
  y: number;
  t: number;
  axis: ZoomDrag | null;
  dx: number;
  dy: number;
  /** The picture's view when the finger went down. */
  from: ZoomView;
  box: Geometry;
  /** Down on the picture itself (a double tap counts only there). */
  onMedia: boolean;
}
interface Pinch {
  d0: number;
  c0: Pt;
  /** The first middle of the fingers, from the picture's centre. */
  focus: Pt;
  from: ZoomView;
  box: Geometry;
}
/** The picture's centre on screen, and the box the zoom rule works in. */
interface Geometry extends ZoomBox {
  cx: number;
  cy: number;
}

/** A clip's control bar (play, the scrubber) takes the touches in this strip at the bottom of the video (px). */
const CONTROLS_H = 56;
const SETTLE_MS = 250;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_REACH = 40;

export interface ViewerGestures {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

/**
 * Handlers for the viewer's overlay. `zoomable` is true for a still picture (not a clip or a GIF video, which keep
 * their own controls). `index` going to another item puts the zoom back to fit.
 */
export function useViewerGestures(opts: {
  box: React.RefObject<HTMLDivElement | null>;
  count: number;
  index: number;
  zoomable: boolean;
  onClose: () => void;
  onPage: (to: number) => void;
}): ViewerGestures {
  const o = useRef(opts);
  o.current = opts;
  const pointers = useRef(new Map<number, Pt>());
  const drag = useRef<Drag | null>(null);
  const pinch = useRef<Pinch | null>(null);
  const view = useRef<ZoomView>(FIT_VIEW);
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null);

  // A new item opens at fit.
  useEffect(() => {
    view.current = FIT_VIEW;
    lastTap.current = null;
  }, [opts.index]);

  const media = () => o.current.box.current?.querySelector<HTMLElement>(":scope > img, :scope > video") ?? null;

  /** Draws the picture at `v`, moved by (ox, oy) and scaled by k on top (a pull or a page turn), over `ms`. */
  const paint = (v: ZoomView, ox = 0, oy = 0, k = 1, ms = 0) => {
    const m = media();
    if (!m) return;
    m.style.transition = ms ? `transform ${ms}ms cubic-bezier(.32,.72,0,1)` : "none";
    m.style.transform = v === FIT_VIEW && !ox && !oy && k === 1 ? "" : `translate(${v.x + ox}px, ${v.y + oy}px) scale(${v.scale * k})`;
  };
  const rest = () => {
    paint(view.current, 0, 0, 1, SETTLE_MS);
    o.current.box.current?.style.removeProperty("--pull");
  };

  const geometry = (): Geometry => {
    const ov = o.current.box.current;
    const m = media();
    if (!ov || !m) return { viewW: 0, viewH: 0, fitW: 0, fitH: 0, cx: 0, cy: 0 };
    const r = ov.getBoundingClientRect();
    const cs = getComputedStyle(ov);
    const pad = (side: string) => parseFloat(cs.getPropertyValue(`padding-${side}`)) || 0;
    // Layout sizes, not the drawn ones: the picture may be mid-transition.
    return {
      viewW: ov.clientWidth - pad("left") - pad("right"),
      viewH: ov.clientHeight - pad("top") - pad("bottom"),
      fitW: m.offsetWidth,
      fitH: m.offsetHeight,
      cx: r.left + ov.clientLeft + m.offsetLeft + m.offsetWidth / 2,
      cy: r.top + ov.clientTop + m.offsetTop + m.offsetHeight / 2,
    };
  };

  const begin = (x: number, y: number, onMedia: boolean) => {
    drag.current = { x, y, t: performance.now(), axis: null, dx: 0, dy: 0, from: view.current, box: geometry(), onMedia };
  };
  const startPinch = () => {
    // A second finger ends a drag in progress; the picture goes back to where it was.
    if (drag.current?.axis) rest();
    drag.current = null;
    if (!o.current.zoomable) return;
    const [a, b] = [...pointers.current.values()];
    const box = geometry();
    const c0 = { x: (a!.x + b!.x) / 2, y: (a!.y + b!.y) / 2 };
    pinch.current = { d0: Math.max(1, Math.hypot(a!.x - b!.x, a!.y - b!.y)), c0, focus: { x: c0.x - box.cx, y: c0.y - box.cy }, from: view.current, box };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse") return;
    // The buttons and a clip's own controls (the scrubber) keep their touches; the rest of the clip pulls to close.
    const target = e.target as Element;
    if (target.closest("a, button")) return;
    const clip = target.closest("video[controls]");
    if (clip && e.clientY > clip.getBoundingClientRect().bottom - CONTROLS_H) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) begin(e.clientX, e.clientY, e.target === media());
    else if (pointers.current.size === 2) startPinch();
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const p = pinch.current;
    if (p) {
      const [a, b] = [...pointers.current.values()];
      if (!a || !b) return;
      const c = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      view.current = pinchView(p.from, Math.hypot(a.x - b.x, a.y - b.y) / p.d0, p.focus.x, p.focus.y, c.x - p.c0.x, c.y - p.c0.y, p.box);
      paint(view.current);
      return;
    }
    const d = drag.current;
    if (!d) return;
    if (!d.axis) {
      const axis = zoomedDragAxis(e.clientX - d.x, e.clientY - d.y, o.current.count, d.from, d.box);
      if (!axis) return;
      d.axis = axis;
      // A pull or a pan starts from here (no jump); a page turn counts from the first touch, so a short flick still turns.
      if (axis === "pull" || axis === "pan") {
        d.x = e.clientX;
        d.y = e.clientY;
        d.t = performance.now();
      }
    }
    if (d.axis === "pan") {
      view.current = panView(d.from, e.clientX - d.x, e.clientY - d.y, d.box);
      paint(view.current);
    } else if (d.axis === "pull") {
      d.dy = Math.max(0, e.clientY - d.y);
      paint(view.current, 0, d.dy, Math.max(0.85, 1 - d.dy / 2000));
      o.current.box.current?.style.setProperty("--pull", String(Math.min(1, d.dy / 300)));
    } else if (d.axis === "page") {
      d.dx = e.clientX - d.x;
      paint(view.current, pageOffset(d.dx, o.current.index, o.current.count));
    }
  };

  const end = (e: React.PointerEvent, cancelled: boolean) => {
    if (!pointers.current.delete(e.pointerId)) return;
    if (pinch.current) {
      // The fingers lift: below fit springs back, past 4x is cut. A finger left down starts a fresh drag.
      view.current = settleView(view.current, pinch.current.box);
      pinch.current = null;
      paint(view.current, 0, 0, 1, SETTLE_MS);
      const [left] = [...pointers.current.values()];
      drag.current = null;
      if (left) begin(left.x, left.y, true);
      return;
    }
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.axis) {
      if (!cancelled) tap(e, d);
      return;
    }
    if (d.axis === "none" || d.axis === "pan") return;
    if (cancelled) return rest();
    const ms = performance.now() - d.t;
    if (d.axis === "pull") {
      if (closesOnPull(d.dy, ms)) return o.current.onClose();
      return rest();
    }
    const { index, count, box, onPage } = { ...o.current, box: o.current.box.current };
    const next = pageAfterDrag(index, count, d.dx, box?.clientWidth ?? window.innerWidth, ms);
    if (next === index) rest();
    else onPage(next);
  };

  /** A finger lifted without moving past the slop: the second of two quick taps on the picture zooms. */
  const tap = (e: React.PointerEvent, d: Drag) => {
    const now = performance.now();
    if (!o.current.zoomable || !d.onMedia || now - d.t > DOUBLE_TAP_MS) return (lastTap.current = null);
    const prev = lastTap.current;
    if (!prev || now - prev.t > DOUBLE_TAP_MS || Math.hypot(e.clientX - prev.x, e.clientY - prev.y) > DOUBLE_TAP_REACH) {
      lastTap.current = { t: now, x: e.clientX, y: e.clientY };
      return;
    }
    lastTap.current = null;
    const box = geometry();
    view.current = doubleTapView(view.current, e.clientX - box.cx, e.clientY - box.cy, box);
    paint(view.current, 0, 0, 1, SETTLE_MS);
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: (e) => end(e, false),
    onPointerCancel: (e) => end(e, true),
  };
}
